import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { DropdownModule } from 'primeng/dropdown';
import { WalletService } from '../../../../services/wallet.service';
import { Wallet, WalletStats } from '../../../../models/Wallet';
import { WalletTransaction, RechargeRequest } from '../../../../models/WalletTransaction';
import { ToastrService } from 'ngx-toastr';
import { GenericTable } from '../../../../models/GenericCRUD/GenericTable';
import { GenericTableComponent } from '../../../../shared/components/generic-crud/generic-table/generic-table.component';
import { GenericFormComponent } from '../../../../shared/components/generic-crud/generic-form/generic-form.component';
import { GenericForm, GenericFormField } from '../../../../models/GenericCRUD/GenericForm';
import { GenericCrudService } from '../../../../services/generic-crud.service';
import { PaymentData } from '../../../../shared/components/payment-method-selector/payment-method-selector.component';

@Component({
  selector: 'app-user-wallet',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    ButtonModule,
    InputNumberModule,
    InputTextModule,
    InputTextareaModule,
    TagModule,
    TooltipModule,
    DropdownModule,
    GenericTableComponent,
    GenericFormComponent
  ],
  templateUrl: './user-wallet.component.html',
  styleUrl: './user-wallet.component.css'
})
export class UserWalletComponent implements OnInit, OnChanges {
  @Input() userId!: number;
  @Input() productStates: any[] = []; // Recibimos los estados de producto para el selector
  @Input() activeProductId: number | null = null; // Carnet activo controlado desde la ficha

  wallet: Wallet | null = null;
  stats: WalletStats | null = null;
  transactions: WalletTransaction[] = [];
  loading = false;
  transactionsTable?: GenericTable<WalletTransaction>;
  hasTransactions = false;
  charges: WalletTransaction[] = [];

  // Selector de producto
  selectedProductId: number | null = null;
  productOptions: any[] = [];

  // Diálogos y Formularios Generic
  showGenericDialog = false;
  currentGenericForm: GenericForm | null = null;
  genericFormInitialData: any = {};

  // Custom recharge form
  showRechargeForm = false;
  rechargeSubmitting = false;
  rechargeError: string | null = null;
  rechargePaymentOptions: any[] = [];
  rechargeFormData: {
    amount: number | null;
    payment_method: string;
  } = { amount: null, payment_method: 'external' };
  rechargePaymentData: PaymentData = { payment_method: 'cash', pos_terminal_id: null, cash_amount: null, card_amount: null, valid: true };

  constructor(
    private walletService: WalletService,
    private toastr: ToastrService,
    private genericCrudService: GenericCrudService
  ) { }

  ngOnInit() {
    if (this.userId) {
      this.initProductOptions();
      this.loadWallet();
      this.initializeTransactionsTable();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['productStates'] && !changes['productStates'].firstChange) {
      this.initProductOptions();
    }

    // El carnet activo lo controla la ficha: al cambiarlo, recargar el monedero.
    if (changes['activeProductId'] && this.activeProductId != null) {
      this.selectedProductId = this.activeProductId;
      this.loadWallet();
      this.initializeTransactionsTable();
    }

    if (changes['userId'] && !changes['userId'].firstChange) {
      this.selectedProductId = null;
      this.initProductOptions();
      this.loadWallet();
      this.initializeTransactionsTable();
    }
  }

  initProductOptions() {
    const previousSelection = this.selectedProductId;

    // Solo mostramos wallets por producto (eliminamos el wallet general)
    this.productOptions = this.productStates.map(ps => ({
      label: ps.product?.name || 'Producto sin nombre',
      value: ps.product_id
    }));

    // Si la ficha controla el carnet activo, usarlo como fuente de verdad.
    if (this.activeProductId != null) {
      this.selectedProductId = this.activeProductId;
      return;
    }

    // Verificar si la selección anterior sigue siendo válida
    const isValidSelection = this.productOptions.some(opt => opt.value === previousSelection);

    if (!isValidSelection && this.productOptions.length > 0) {
      // Seleccionar el primer producto disponible
      this.selectedProductId = this.productOptions[0].value;
    } else if (this.productOptions.length === 0) {
      this.selectedProductId = null;
    }
  }

  onProductChange() {
    this.loadWallet();
    this.initializeTransactionsTable();
  }

  initializeTransactionsTable() {
    const loadSlug = `/wallet/${this.userId}/transactions${this.selectedProductId ? '?product_id=' + this.selectedProductId : ''}`;

    this.transactionsTable = {
      columns: [
        { name: 'Fecha', active: true, type: 'date-time', key: 'created_at', sort: false },
        { name: 'Tipo', active: true, type: 'text', key: 'type_label', sort: false },
        { name: 'Concepto', active: true, type: 'text', key: 'concept', sort: false },
        { name: 'Método Pago', active: true, type: 'text', key: 'payment_method_label', sort: false },
        { name: 'Detalle Pago', active: true, type: 'text', key: 'payment_detail', sort: false },
        { name: 'Monto (€)', active: true, type: 'currency', key: 'amount', sort: false },
        { name: 'Prác. Abierto', active: true, type: 'text', key: 'practice_amount_normal', sort: false },
        { name: 'Prác. Maniobras', active: true, type: 'text', key: 'practice_amount_maniobras', sort: false },
        { name: 'Saldo (€)', active: true, type: 'currency', key: 'balance_after', sort: false },
        { name: 'Saldo Abierto', active: true, type: 'text', key: 'practice_balance_normal_after', sort: false },
        { name: 'Saldo Maniobras', active: true, type: 'text', key: 'practice_balance_maniobras_after', sort: false },
        { name: 'Creado Por', active: false, type: 'text', key: ['created_by', 'name'], sort: false }
      ],
      items: [],
      loadItemsSlug: loadSlug,
      type: 'generic',
      paginate: true,
      name: 'Historial de Transacciones',
      actions: [],
      bulkActions: [],
      defaultSort: { column: 'created_at', order: 'desc' }
    };
  }

  loadWallet() {
    this.loading = true;
    this.walletService.getWallet(this.userId, this.selectedProductId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.wallet = response.data.wallet;
          this.stats = response.data.stats;
          this.hasTransactions = (this.stats?.total_transactions ?? 0) > 0;
        }
        this.loading = false;
      },
      error: (error) => {
        this.toastr.error('Error al cargar el wallet');
        this.loading = false;
      }
    });
  }

  openActionDialog(action: 'recharge' | 'charge' | 'refund' | 'adjustment' | 'buyPractices') {
    this.genericFormInitialData = { product_id: this.selectedProductId };

    switch (action) {
      case 'recharge':
        // Cargamos los saldos de otros wallets de producto para mostrarlos como opciones de transferencia
        this.loadAllWalletBalancesAndOpenRecharge();
        break;
      case 'charge':
        this.currentGenericForm = this.getChargeFormConfig();
        this.showGenericDialog = true;
        break;
      case 'adjustment':
        this.currentGenericForm = this.getAdjustmentFormConfig();
        this.showGenericDialog = true;
        break;
      case 'refund':
        this.loadChargesAndOpenRefund();
        break;
      case 'buyPractices':
        this.currentGenericForm = this.getBuyPracticesFormConfig();
        this.showGenericDialog = true;
        break;
    }
  }

  handleFormSuccess(data: any) {
    this.showGenericDialog = false;
    this.loadWallet();
    this.hasTransactions = true;
    this.initializeTransactionsTable();
  }

  loadChargesAndOpenRefund() {
    this.walletService.getTransactions(this.userId, 100, this.selectedProductId).subscribe({
      next: (response: any) => {
        if (response.success && response.data) {
          const transactions = response.data.data || response.data;
          this.charges = transactions.filter((t: WalletTransaction) => t.type === 'charge');
          this.currentGenericForm = this.getRefundFormConfig(this.charges);
          this.showGenericDialog = true;
        }
      },
      error: () => this.toastr.error('Error al cargar cargos')
    });
  }

  loadAllWalletBalancesAndOpenRecharge() {
    const walletBalances: { [key: string]: number } = {};
    const otherProducts = this.productStates.filter(ps => ps.product_id !== this.selectedProductId);

    const openRechargeForm = () => {
      this.rechargePaymentOptions = [{ id: 'external', name: 'Pago Externo' }];
      this.productStates.forEach(ps => {
        if (ps.product_id !== this.selectedProductId) {
          const balance = walletBalances[`product_${ps.product_id}`] || 0;
          if (balance > 0) {
            this.rechargePaymentOptions.push({
              id: `product_wallet_${ps.product_id}`,
              name: `Transferir desde: ${ps.product?.name || 'Producto'} (Saldo: ${this.formatAmount(balance)})`
            });
          }
        }
      });
      this.rechargeFormData = { amount: null, payment_method: 'external' };
      this.rechargePaymentData = { payment_method: 'cash', pos_terminal_id: null, cash_amount: null, card_amount: null, valid: true };
      this.rechargeError = null;
      this.showRechargeForm = true;
    };

    if (otherProducts.length === 0) {
      openRechargeForm();
      return;
    }

    let loadedCount = 0;
    otherProducts.forEach(ps => {
      this.walletService.getWallet(this.userId, ps.product_id).subscribe({
        next: (response) => {
          walletBalances[`product_${ps.product_id}`] = response.data?.wallet?.balance || 0;
          loadedCount++;
          if (loadedCount === otherProducts.length) openRechargeForm();
        },
        error: () => {
          walletBalances[`product_${ps.product_id}`] = 0;
          loadedCount++;
          if (loadedCount === otherProducts.length) openRechargeForm();
        }
      });
    });
  }

  get isExternalPayment(): boolean {
    return !this.rechargeFormData.payment_method || this.rechargeFormData.payment_method === 'external';
  }

  onRechargePaymentDataChange(data: PaymentData) {
    this.rechargePaymentData = data;
  }

  isRechargeFormValid(): boolean {
    if (!this.rechargeFormData.amount || this.rechargeFormData.amount <= 0) return false;
    if (this.isExternalPayment) {
      return this.rechargePaymentData.valid;
    }
    return true;
  }

  submitRechargeForm() {
    if (!this.isRechargeFormValid()) return;
    this.rechargeSubmitting = true;
    this.rechargeError = null;

    const payload: any = {
      amount: this.rechargeFormData.amount,
      product_id: this.selectedProductId,
      payment_method: this.rechargeFormData.payment_method || 'external',
    };

    if (this.isExternalPayment) {
      payload.real_payment_method = this.rechargePaymentData.payment_method;
      if (this.rechargePaymentData.payment_method === 'card' || this.rechargePaymentData.payment_method === 'split') {
        payload.pos_terminal_id = this.rechargePaymentData.pos_terminal_id;
      }
      if (this.rechargePaymentData.payment_method === 'split') {
        payload.cash_amount = this.rechargePaymentData.cash_amount;
        payload.card_amount = this.rechargePaymentData.card_amount;
      }
    }

    this.genericCrudService.create(`/wallet/${this.userId}/recharge`, payload).subscribe({
      next: res => {
        this.rechargeSubmitting = false;
        if (res.success) {
          this.showRechargeForm = false;
          this.toastr.success('Operación realizada correctamente');
          this.loadWallet();
          this.hasTransactions = true;
          this.initializeTransactionsTable();
        } else {
          this.rechargeError = res.message || 'Error en la operación';
        }
      },
      error: err => {
        this.rechargeSubmitting = false;
        this.rechargeError = err?.error?.message || 'Error en la operación';
      }
    });
  }

  closeRechargeForm() {
    this.showRechargeForm = false;
  }

  getChargeFormConfig(): GenericForm {
    return {
      formTitle: 'Realizar Cargo',
      formButtonTitle: 'Confirmar Cargo',
      createButtonTitle: '',
      createApiSlug: `/wallet/${this.userId}/charge`,
      method: 'POST',
      name: 'chargeForm',
      successTitle: 'Cargo realizado',
      errorTitle: 'Error al realizar cargo',
      width: 'md',
      cols: 1,
      fields: [
        { name: 'amount', label: 'Monto (€)', type: 'number', validations: 'required', colspan: 1 },
        { name: 'concept', label: 'Concepto', type: 'textarea', validations: 'required', colspan: 1 },
        { name: 'product_id', label: '', type: 'hidden', validations: '', colspan: 1 }
      ]
    };
  }

  getAdjustmentFormConfig(): GenericForm {
    return {
      formTitle: 'Ajuste de Saldo',
      formButtonTitle: 'Confirmar Ajuste',
      createButtonTitle: '',
      createApiSlug: `/wallet/${this.userId}/adjustment`,
      method: 'POST',
      name: 'adjustmentForm',
      successTitle: 'Ajuste realizado',
      errorTitle: 'Error al realizar ajuste',
      width: 'md',
      cols: 1,
      fields: [
        { name: 'amount', label: 'Monto (€) (puede ser negativo)', type: 'number', validations: 'required', colspan: 1 },
        { name: 'concept', label: 'Concepto', type: 'textarea', validations: 'required', colspan: 1 },
        { name: 'product_id', label: '', type: 'hidden', validations: '', colspan: 1 }
      ]
    };
  }

  getRefundFormConfig(charges: WalletTransaction[]): GenericForm {
    const chargeOptions = charges.map(c => ({
      id: c.id,
      name: `${this.formatAmount(c.amount)} - ${c.concept} (${new Date(c.created_at).toLocaleDateString()})`
    }));

    return {
      formTitle: 'Reembolso',
      formButtonTitle: 'Confirmar Reembolso',
      createButtonTitle: '',
      createApiSlug: `/wallet/${this.userId}/refund`,
      method: 'POST',
      name: 'refundForm',
      successTitle: 'Reembolso realizado',
      errorTitle: 'Error al realizar reembolso',
      width: 'md',
      cols: 1,
      fields: [
        {
          name: 'charge_id',
          label: 'Seleccionar Cargo',
          type: 'select',
          optionsList: chargeOptions,
          validations: 'required',
          singleSelection: true,
          colspan: 1
        },
        { name: 'product_id', label: '', type: 'hidden', validations: '', colspan: 1 }
      ]
    };
  }

  getBuyPracticesFormConfig(): GenericForm {
    return {
      formTitle: 'Comprar Prácticas',
      formButtonTitle: 'Confirmar Compra',
      createButtonTitle: '',
      createApiSlug: `/wallet/${this.userId}/buy-practices`,
      method: 'POST',
      name: 'buyPracticesForm',
      successTitle: 'Prácticas compradas',
      errorTitle: 'Error al comprar prácticas',
      width: 'md',
      cols: 2,
      fields: [
        {
          name: 'practice_type',
          label: 'Tipo de Práctica',
          type: 'select',
          optionsList: [
            { id: 'Abierto', name: 'Prácticas de Abierto' },
            { id: 'Maniobras', name: 'Prácticas de Maniobras' }
          ],
          validations: 'required',
          singleSelection: true,
          colspan: 2,
          testValue: 'Abierto'
        },
        { name: 'number_of_practices', label: 'Número de Prácticas', type: 'number', validations: 'required', colspan: 1, testValue: '1' },
        { name: 'price_per_practice', label: 'Precio por Práctica (€)', type: 'number', validations: 'required', colspan: 1 },
        { name: 'concept', label: 'Concepto', type: 'text', validations: '', colspan: 2, testValue: 'Compra de bono de prácticas' },
        { name: 'product_id', label: '', type: 'hidden', validations: '', colspan: 1 }
      ]
    };
  }

  getTransactionTypeSeverity(type: string): 'success' | 'danger' | 'info' | 'warning' | 'secondary' {
    return this.walletService.getTransactionTypeColor(type) as any;
  }

  formatAmount(amount: number): string {
    return this.walletService.formatAmount(amount);
  }

  getSelectedProductLabel(): string {
    if (this.selectedProductId === null) {
      return 'Sin producto seleccionado';
    }
    const option = this.productOptions.find(p => p.value === this.selectedProductId);
    return option?.label || 'Producto desconocido';
  }
}
